import type { OpenSource } from '@parcel/analytics';
import type { TableFilter } from '@parcel/filters';
import {
  Button,
  DeleteViewDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  ViewOptionsMenu,
  ViewTabs,
  WORKSPACE_VIEW_DELETE_DESCRIPTION,
} from '@parcel/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouterState } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import { SlidersHorizontal } from 'lucide-react';
import * as React from 'react';
import { AccessEmailsAction } from '../components/contacts/access-emails-action.js';
import { ContactsDataTable } from '../components/contacts/contacts-data-table.js';
import {
  CONTACTS_DEFAULT_SORTING,
  useContactsTable,
} from '../components/contacts/use-contacts-table.js';
import { BrowseSummaryMeta } from '../components/data-table/browse-summary-meta.js';
import { ViewSelectionToolbar } from '../components/data-table/view-selection-toolbar.js';
import { EnrichScopeProvider } from '../components/enrich/enrich-scope.js';
import { PageHeader } from '../components/page-header.js';
import { useAuth } from '../lib/auth.js';
import { type ApiBrowseSummary, BROWSE_PAGE_SIZE } from '../lib/browse-query.js';
import {
  useContactBrowseSummary,
  useContactSearchPreview,
  useWorkspaceContacts,
} from '../lib/contacts.js';
import {
  useEntityBrowseFirstRowsTiming,
  useEntityBrowseLaterPageSignals,
} from '../lib/entity-browse-observability.js';
import { filterEntriesKey } from '../lib/filter-params.js';
import { useAskFilters } from '../lib/filters-ask.js';
import { listTransitionKey } from '../lib/list-transition.js';
import { useRecordDetail } from '../lib/record-detail.js';
import {
  DRAFT_VIEW_ID,
  DRAFT_VIEW_NAME,
  createDraftSavedView,
  isConfirmedViewName,
} from '../lib/saved-view-drafts.js';
import { useScore } from '../lib/scores.js';
import { isSupabaseConfigured } from '../lib/supabase.js';
import {
  applyAskWhere,
  hasTableFilter,
  makeTableSearchValidator,
  tableFilterEntries,
  useTableFilterSearch,
  useTableViewSearchParam,
} from '../lib/table-filter.js';
import { CONTACTS_BASE_PRESENTATION } from '../lib/table-views.js';
import {
  useEntityListViewScope,
  useWorkspaceTableViews,
} from '../lib/use-workspace-table-views.js';
import { runChunkedMembership, selectionMap } from '../lib/view-selection-state.js';
import { useWhereOptions } from '../lib/where-options.js';
import {
  type WorkspaceView,
  useFavoriteViewMutations,
  useFavoriteViews,
  useWorkspaceViewMutations,
  useWorkspaceViews,
} from '../lib/workspace-views.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../lib/workspaces.js';

export const Route = createFileRoute('/contacts')({
  component: ContactsPage,
  validateSearch: makeTableSearchValidator({ view: 'uuid' }),
});

// Debounce the record-preview typeahead so typing doesn't fire an HTTP request per keystroke.
const SEARCH_DEBOUNCE_MS = 250;

type FilterLookup = { text: string; activeField: string | null };

export function ContactsPage() {
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { session } = useAuth();
  const userId = session?.user.id;
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspaceId = workspaceQuery.data?.id;

  const { filter: filterState, setFilter: setFilterState } = useTableFilterSearch('contact');
  const { activeViewId: urlViewId, setActiveViewId, urlHasFilterKeys } = useTableViewSearchParam();
  const filterEntries = React.useMemo(() => tableFilterEntries(filterState), [filterState]);
  // Mirror filterState so the async ask handler merges into the latest state.
  const filterStateRef = React.useRef(filterState);
  filterStateRef.current = filterState;
  const { mutateAsync: askFilters } = useAskFilters();
  const handleAsk = React.useCallback(
    async (query: string) => {
      if (!workspaceId) throw new Error('workspace not loaded');
      const result = await askFilters({ workspaceId, entity: 'contact', query });
      setFilterState(applyAskWhere(filterStateRef.current, result));
      return result;
    },
    [askFilters, workspaceId, setFilterState],
  );

  const viewsQuery = useWorkspaceViews(workspaceId, 'contact');
  const viewsListStatus = viewsQuery.isPending
    ? 'pending'
    : viewsQuery.isError
      ? 'error'
      : 'success';
  const listScope = useEntityListViewScope({
    views: viewsQuery.data,
    urlViewId,
    dataset: 'contact',
    listStatus: viewsListStatus,
    urlHasFilterKeys,
    filterState,
  });

  const browseMode = true;
  const [sorting, setSorting] = React.useState<SortingState>(CONTACTS_DEFAULT_SORTING);
  const summaryScopeKey = React.useMemo(
    () =>
      `${workspaceId ?? ''}|${urlViewId ?? ''}|${filterEntriesKey(filterEntries)}|${listScope.staticViewId ?? ''}`,
    [filterEntries, listScope.staticViewId, urlViewId, workspaceId],
  );
  const summaryQuery = useContactBrowseSummary(workspaceId, filterEntries, listScope.staticViewId, {
    enabled: listScope.ready,
  });
  const [acceptedSummary, setAcceptedSummary] = React.useState<{
    workspaceId: string;
    scopeKey: string;
    summary: ApiBrowseSummary;
  } | null>(null);
  React.useEffect(() => {
    setAcceptedSummary((current) => {
      if (!current || current.workspaceId === workspaceId) return current;
      return null;
    });
  }, [workspaceId]);
  React.useEffect(() => {
    if (!workspaceId || !listScope.ready || !summaryQuery.data) return;
    setAcceptedSummary((current) => {
      if (current?.scopeKey === summaryScopeKey) return current;
      return { workspaceId, scopeKey: summaryScopeKey, summary: summaryQuery.data };
    });
  }, [listScope.ready, summaryQuery.data, summaryScopeKey, workspaceId]);
  const accepted =
    listScope.ready && acceptedSummary?.scopeKey === summaryScopeKey
      ? acceptedSummary.summary
      : undefined;

  const contactsQuery = useWorkspaceContacts(workspaceId, filterEntries, listScope.staticViewId, {
    enabled: listScope.ready,
    browseMode,
    sorting,
  });
  const contacts = contactsQuery.data.contacts;

  // Identity of the active view + filters. A view/filter change navigates the
  // URL, and the list query only re-keys once that navigation commits (a few
  // hundred ms later); until then the old rows sit on screen with no loading
  // state. Flip a flag synchronously on the change so the skeleton shows
  // immediately, then clear it once this identity moves off the pre-change value
  // AND the query settles. The view id MUST be part of the identity: two views
  // can share a filter (e.g. Architects vs Architects New), and without it a
  // switch between them never registers as changed and the skeleton sticks.
  const listKey = listTransitionKey(urlViewId, filterEntriesKey(filterEntries));
  const [isListTransitioning, setIsListTransitioning] = React.useState(false);
  const transitionFromKeyRef = React.useRef(listKey);
  const beginListTransition = React.useCallback(() => {
    transitionFromKeyRef.current = listKey;
    setIsListTransitioning(true);
  }, [listKey]);
  React.useEffect(() => {
    if (
      isListTransitioning &&
      listKey !== transitionFromKeyRef.current &&
      !contactsQuery.isFetching &&
      !contactsQuery.isPlaceholderData
    ) {
      setIsListTransitioning(false);
    }
  }, [isListTransitioning, listKey, contactsQuery.isFetching, contactsQuery.isPlaceholderData]);
  const handleFilterStateChange = React.useCallback(
    (next: TableFilter) => {
      // A refused or unchanged filter never moves the list, so it starts no loading state.
      if (setFilterState(next)) beginListTransition();
    },
    [beginListTransition, setFilterState],
  );

  const [filterLookup, setFilterLookup] = React.useState<FilterLookup>({
    text: '',
    activeField: null,
  });
  const [debouncedLookup, setDebouncedLookup] = React.useState<FilterLookup>({
    text: '',
    activeField: null,
  });
  React.useEffect(() => {
    const handle = window.setTimeout(
      () =>
        setDebouncedLookup({
          text: filterLookup.text.trim(),
          activeField: filterLookup.activeField,
        }),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [filterLookup]);

  const previewQuery = useContactSearchPreview(
    workspaceId,
    debouncedLookup.text,
    filterEntries,
    listScope.staticViewId,
  );
  const previewRecords = React.useMemo(
    () =>
      (previewQuery.data?.contacts ?? []).map((c) => ({
        id: c.id,
        label: c.name,
        sublabel: c.account_name,
      })),
    [previewQuery.data],
  );

  const prospectScore = useScore(workspaceId, 'prospect').data;
  const whereOptions = useWhereOptions(workspaceId, 'contact');

  const { table } = useContactsTable(contacts, {
    browseMode,
    sorting,
    onSortingChange: setSorting,
    prospectScore,
  });
  const { record, open: openRecord, close: closeRecord } = useRecordDetail();
  const openContactId = record?.type === 'contact' ? record.id : null;
  // A null open only comes from the table dropping the row on a scope change: keep that scope.
  const handleOpenContact = React.useCallback(
    (id: string | null, source: OpenSource = 'table') => {
      if (id) openRecord({ type: 'contact', id }, { source });
      else closeRecord({ unwind: false });
    },
    [closeRecord, openRecord],
  );
  const [browseRefreshRevision, setBrowseRefreshRevision] = React.useState(0);
  const tableScopeKey = React.useMemo(
    () =>
      `${workspaceId ?? ''}|${urlViewId ?? ''}|${filterEntriesKey(filterEntries)}|${listScope.staticViewId ?? ''}|${sorting.map((sort) => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`).join(',')}|${browseRefreshRevision}`,
    [browseRefreshRevision, filterEntries, listScope.staticViewId, sorting, urlViewId, workspaceId],
  );
  useEntityBrowseFirstRowsTiming(
    'contact',
    browseMode,
    tableScopeKey,
    contacts.length,
    contactsQuery.isLoading,
  );
  const loadedPageCount = contacts.length === 0 ? 0 : Math.ceil(contacts.length / BROWSE_PAGE_SIZE);
  const { wrapFetchNextPage } = useEntityBrowseLaterPageSignals(
    'contact',
    browseMode,
    contactsQuery.isFetchNextPageError ?? false,
    loadedPageCount,
  );
  const instrumentedFetchNextPage = React.useMemo(
    () => wrapFetchNextPage(contactsQuery.fetchNextPage ?? (async () => undefined)),
    [contactsQuery.fetchNextPage, wrapFetchNextPage],
  );
  const handleBrowseRefresh = React.useCallback(() => {
    queryClient.removeQueries({
      queryKey: [
        'contacts-browse',
        workspaceId,
        filterEntriesKey(filterEntries),
        listScope.staticViewId ?? null,
      ],
    });
    table.resetRowSelection();
    setBrowseRefreshRevision((revision) => revision + 1);
    if (workspaceId && summaryQuery.data) {
      setAcceptedSummary({
        workspaceId,
        scopeKey: summaryScopeKey,
        summary: summaryQuery.data,
      });
    }
    void summaryQuery.refetch();
  }, [
    filterEntries,
    listScope.staticViewId,
    queryClient,
    summaryQuery,
    summaryScopeKey,
    table,
    workspaceId,
  ]);

  const savedViews = useWorkspaceTableViews({
    workspaceId,
    dataset: 'contact',
    table,
    basePresentation: CONTACTS_BASE_PRESENTATION,
    filterState,
    setFilterState,
    activeViewId: urlViewId,
    setActiveViewId,
    urlHasFilterKeys,
  });
  const favoritesQuery = useFavoriteViews(workspaceId, userId);
  const favoriteMutations = useFavoriteViewMutations(workspaceId, userId);
  const activeViewIsFavorite = Boolean(
    savedViews.activeView &&
      (favoritesQuery.data?.some((view) => view.id === savedViews.activeView?.id) ??
        savedViews.activeView.is_favorite),
  );
  const toggleFavorite = (view: WorkspaceView) => {
    const isFavorite =
      favoritesQuery.data?.some((favorite) => favorite.id === view.id) ?? Boolean(view.is_favorite);
    if (isFavorite) favoriteMutations.unfavorite.mutate(view.id);
    else favoriteMutations.favorite.mutate({ view_id: view.id });
  };
  const [editingViewId, setEditingViewId] = React.useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [editingValue, setEditingValue] = React.useState('');
  const [draftView, setDraftView] = React.useState<ReturnType<typeof createDraftSavedView> | null>(
    null,
  );
  const visibleViews = [
    ...savedViews.views.map((view) => ({ id: view.id, name: view.name })),
    ...(draftView ? [{ id: draftView.id, name: draftView.name }] : []),
  ];
  const activeViewId = draftView ? draftView.id : savedViews.activeViewId;
  const isViewTabsDirty = draftView ? false : savedViews.canSaveAsNew;
  // Skeleton on: initial load, the keepPreviousData fetch window, AND the
  // click->navigation gap before the query re-keys (isListTransitioning).
  const isListLoading =
    contactsQuery.isLoading || contactsQuery.isPlaceholderData || isListTransitioning;

  const startNewView = () => {
    const view = createDraftSavedView({
      columnFilters: [],
      sorting: [],
      columnVisibility: {},
      globalFilter: '',
    });
    setDraftView(view);
    setEditingViewId(DRAFT_VIEW_ID);
    setEditingValue(DRAFT_VIEW_NAME);
  };

  const finishEditingView = () => {
    const nextName = editingValue.trim();
    if (draftView && editingViewId === DRAFT_VIEW_ID) {
      if (isConfirmedViewName(nextName)) {
        void savedViews.saveAsNew(nextName);
      }

      setDraftView(null);
      setEditingViewId(null);
      setEditingValue('');
      return;
    }

    if (editingViewId !== savedViews.activeViewId) {
      setEditingViewId(null);
      setEditingValue('');
      return;
    }

    if (nextName) {
      void savedViews.renameView(nextName);
    }

    setEditingViewId(null);
    setEditingValue('');
  };

  const cancelEditingView = () => {
    setDraftView(null);
    setEditingViewId(null);
    setEditingValue('');
  };

  const applyBaseView = () => {
    if (urlViewId !== null || hasTableFilter(filterState)) beginListTransition();
    setDraftView(null);
    savedViews.applyBaseView();
  };

  const applySavedView = (viewId: string) => {
    if (viewId !== urlViewId) beginListTransition();
    setDraftView(null);
    savedViews.applyView(viewId);
  };

  const viewMutations = useWorkspaceViewMutations(workspaceId, 'contact');
  const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
  const selectedIds = selectedRows.map((row) => row.id);
  const staticViews = React.useMemo(
    () => savedViews.views.filter((view) => view.view_type === 'static'),
    [savedViews.views],
  );
  const activeStaticView =
    savedViews.activeView?.view_type === 'static' ? savedViews.activeView : null;

  return (
    <main className="flex min-h-0 min-w-0 flex-1 flex-col">
      <PageHeader
        title="Contacts"
        meta={
          isSupabaseConfigured ? (
            <BrowseSummaryMeta
              summaryQuery={summaryQuery}
              baselineDataVersion={accepted?.data_version ?? null}
              onRefresh={handleBrowseRefresh}
            />
          ) : null
        }
      />

      <ViewTabs
        views={visibleViews}
        activeViewId={activeViewId}
        isDirty={isViewTabsDirty}
        editingViewId={editingViewId}
        editingValue={editingValue}
        onEditingValueChange={setEditingValue}
        onCommitEditingView={finishEditingView}
        onCancelEditingView={cancelEditingView}
        onSelectBase={applyBaseView}
        onSelectView={applySavedView}
        onSaveAsNew={startNewView}
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-2 rounded-full">
                <SlidersHorizontal className="h-4 w-4" />
                <span className="inline-flex items-center gap-2">
                  Views
                  {savedViews.activeView && savedViews.isDirty ? (
                    <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden />
                  ) : null}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56"
              onCloseAutoFocus={(event) => event.preventDefault()}
            >
              <ViewOptionsMenu
                hasActiveView={Boolean(savedViews.activeView)}
                isDirty={savedViews.isDirty}
                canSaveAsNew={savedViews.canSaveAsNew}
                onSave={savedViews.saveCurrent}
                onSaveAsNew={startNewView}
                isFavorite={activeViewIsFavorite}
                onToggleFavorite={() => {
                  const view = savedViews.activeView;
                  if (view) toggleFavorite(view);
                }}
                onRename={() => {
                  if (!savedViews.activeView) return;
                  setDraftView(null);
                  setEditingViewId(savedViews.activeView.id);
                  setEditingValue(savedViews.activeView.name);
                }}
                onDelete={() => setDeleteDialogOpen(true)}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      <EnrichScopeProvider workspaceId={workspaceId}>
        <ContactsDataTable
          table={table}
          browseMode={browseMode}
          scopeKey={tableScopeKey}
          filterState={filterState}
          onFilterStateChange={handleFilterStateChange}
          options={whereOptions}
          openContactId={openContactId}
          onOpenContactId={handleOpenContact}
          isLoading={isSupabaseConfigured && isListLoading}
          isError={isSupabaseConfigured && contactsQuery.isError}
          hasNextPage={contactsQuery.hasNextPage ?? false}
          isFetchingNextPage={contactsQuery.isFetchingNextPage ?? false}
          isFetchNextPageError={contactsQuery.isFetchNextPageError ?? false}
          fetchNextPage={instrumentedFetchNextPage}
          summaryTotal={accepted?.total ?? summaryQuery.data?.total ?? null}
          recordMatches={previewRecords}
          onRecordInputChange={(text) => setFilterLookup({ text, activeField: null })}
          onSelectRecord={(id) => handleOpenContact(id, 'search')}
          onAsk={workspaceId ? handleAsk : undefined}
          selectionActive={selectedIds.length > 0}
          selectionToolbar={
            <ViewSelectionToolbar
              dataset="contact"
              selectedIds={selectedIds}
              extraActions={<AccessEmailsAction selected={selectedRows} />}
              staticViews={staticViews}
              activeStaticView={activeStaticView}
              onAdd={(viewId) =>
                runChunkedMembership(selectedIds, (chunk) =>
                  viewMutations.addRecords.mutateAsync({ viewId, recordIds: chunk }),
                )
              }
              onCreate={async (name) => {
                const created = await viewMutations.staticCreate.mutateAsync({
                  name,
                  dataset: 'contact',
                  initial_record_ids: selectedIds.slice(0, 2000),
                });
                return { view: created.view, outcomes: created.results };
              }}
              onRemove={
                activeStaticView
                  ? () =>
                      runChunkedMembership(selectedIds, (chunk) =>
                        viewMutations.removeRecords.mutateAsync({
                          viewId: activeStaticView.id,
                          recordIds: chunk,
                        }),
                      )
                  : undefined
              }
              onOpenView={(view) => {
                setDraftView(null);
                savedViews.applyView(view.id);
              }}
              onSelectionChange={(ids) => table.setRowSelection(selectionMap(ids))}
            />
          }
        />
      </EnrichScopeProvider>

      <DeleteViewDialog
        open={deleteDialogOpen}
        viewName={savedViews.activeView?.name ?? 'this View'}
        description={WORKSPACE_VIEW_DELETE_DESCRIPTION}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => {
          setDeleteDialogOpen(false);
          void savedViews.deleteView();
        }}
      />
    </main>
  );
}
